import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast as message } from 'sonner';
import { supabaseClient } from '../../utils/supabase';
import EnhancedDialog from '../EnhancedDialog';
import { queryPolicies } from '../../utils/queryPolicies';

/** Bella sends approval texts from the shared company number. Reps can toggle
 * their own messages; capability holders can toggle any rep. */
export interface ApprovalTextSetting {
  rep_id: number;
  rep_name: string;
  rep_active: boolean;
  is_mine: boolean;
  enabled: boolean;
  rep_phone: string | null;
  template: string;
  can_manage: boolean;
  feature_enabled: boolean;
  /** Shared company number managed by Bella. */
  company_from_phone: string | null;
}

const QUERY_KEY = ['approval-text-settings'] as const;

async function fetchApprovalTextSettings(): Promise<ApprovalTextSetting[]> {
  const { data, error } = await (supabaseClient as any).rpc('approval_text_settings');
  if (error) throw error;
  return (data ?? []) as ApprovalTextSetting[];
}

async function saveApprovalTextSetting(args: { p_rep: number; p_enabled: boolean }): Promise<void> {
  const { error } = await (supabaseClient as any).rpc('set_approval_text_setting', args);
  if (error) throw new Error(error.message);
}

export function ApprovalTextChip({ repId }: { repId: number }): React.ReactElement | null {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: QUERY_KEY, queryFn: fetchApprovalTextSettings, ...queryPolicies.default });
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const row = query.data?.find((setting) => setting.rep_id === repId);
  if (!row) return null;
  const canToggle = row.can_manage || row.is_mine;

  const toggle = async (enabled: boolean) => {
    setSaving(true);
    try {
      await saveApprovalTextSetting({ p_rep: repId, p_enabled: enabled });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      message.success(enabled ? 'Approval text turned on.' : 'Approval text turned off.');
    } catch (error) {
      message.error((error as Error).message || 'Unable to update the approval text.');
    } finally {
      setSaving(false);
    }
  };

  const state = row.enabled ? 'On' : 'Off';
  return (
    <>
      <div className="rep-approval-chip">
        <small>
          APPROVAL TEXT
          <button
            className="rep-chip-edit"
            onClick={() => setOpen(true)}
            title="View approval text"
            aria-label={`View approval text for ${row.rep_name}`}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
          </button>
        </small>
        <label title={canToggle ? `Turn approval text ${row.enabled ? 'off' : 'on'}` : 'Read-only'}>
          <input
            type="checkbox"
            checked={row.enabled}
            disabled={!canToggle || saving}
            onChange={(event) => void toggle(event.target.checked)}
          />
          <b className={row.enabled ? 'accent' : ''}>{saving ? 'Saving…' : state}</b>
        </label>
      </div>
      {open && (
        <ApprovalTextDialog
          setting={row}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function ApprovalTextDialog({ setting, onClose }: {
  setting: ApprovalTextSetting;
  onClose: () => void;
}) {
  const titleCase = (value: string) => value.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());
  const preview = setting.template
    .split('{{rep_first_name}}').join(titleCase(setting.rep_name.trim().split(' ')[0] || '') || 'your manager')
    .split('{{rep_name}}').join(titleCase(setting.rep_name.trim()))
    .split('{{rep_phone}}').join(setting.rep_phone?.trim() || '[rep phone missing]')
    .split('{{merchant_first_name}}').join('Dana')
    .split('{{business_name}}').join("Dana's Bakery");

  return (
    <EnhancedDialog isOpen onClose={onClose} title={`Approval text: ${setting.rep_name}`}>
      <div className="sky-dialog-body rep-contact-dialog">
        {!setting.feature_enabled && (
          <p className="rep-approval-note">Approval texts are not switched on yet. Nothing is sent until they are.</p>
        )}
        <p className="rep-approval-note">
          Bella sends from one shared number for new approvals. The message gives the merchant their main rep&apos;s contact details. Never sent to DND merchants.
        </p>
        <div className="rep-contact-field">
          Sending number
          <span>{setting.company_from_phone || 'Not configured'}</span>
          <span className="rep-approval-hint">Shared company number managed by Bella.</span>
        </div>
        <div className="rep-contact-field">
          Account manager phone
          <span>{setting.rep_phone || 'Missing. Add a contact phone on the rep profile to send approval texts.'}</span>
        </div>
        <div className="rep-contact-field">
          Message preview
          <span className="rep-approval-preview" style={{ whiteSpace: 'pre-line' }}>{preview}</span>
        </div>
      </div>
      <div className="sky-dialog-footer">
        <button className="sky-btn sky-btn-ghost" onClick={onClose}>Close</button>
      </div>
    </EnhancedDialog>
  );
}
